import { defineComponent, h, ref, onUnmounted } from 'vue';
import type { EvidenceSource } from '@knowyet/sdk/evidence';
import type { PreviewRequest } from '@knowyet/preview';
import { filePath } from '@knowyet/sdk/files';

export function sourcePreview(source: EvidenceSource, load: (ref: EvidenceSource['original'], signal: AbortSignal) => Promise<Response>, workspaceId?: string): PreviewRequest {
  const extension = source.rendition ? ({'application/pdf':'.pdf','video/mp4':'.mp4','audio/wav':'.wav'}[source.rendition.blob.media_type] ?? '') : '';
  const ref = source.rendition?.blob ?? source.original;
  return { filename: source.file + extension,
    load: signal => load(ref, signal),
    ...(workspaceId && /^(audio|video)\//.test(ref.media_type) ? {mediaUrl:'/api/gateway'+filePath({kind:'artifact',workspaceId,ref})} : {}) };
}
export const previewMediaType = (source: EvidenceSource) => (source.rendition?.blob ?? source.original).media_type;

export const RenditionNotice = defineComponent({
  props: {
    source: { type: Object as () => EvidenceSource, required: true },
    loadOriginal: { type: Function as unknown as () => (signal: AbortSignal) => Promise<Response>, required: true },
  },
  setup(props) {
    const busy = ref(false), error = ref(''); let controller: AbortController | undefined;
    onUnmounted(() => controller?.abort());
    const download = async () => {
      controller?.abort(); controller = new AbortController(); busy.value = true; error.value = '';
      try {
        const response = await props.loadOriginal(controller.signal);
        if (!response.ok) throw Error('download');
        const blob = await response.blob(); controller.signal.throwIfAborted();
        const url = URL.createObjectURL(blob), link = document.createElement('a');
        link.href = url; link.download = props.source.file.split('/').at(-1) || 'original';
        link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch { if (!controller.signal.aborted) error.value = '原件下载失败，请重试。'; }
      finally { busy.value = false; }
    };
    return () => props.source.rendition ? h('div', { 'data-testid': 'office-rendition' }, [
      h('p', {class:'muted'}, props.source.rendition.policy === 'media-browser/1' ? '当前播放浏览器兼容版本，时间定位对应原视频或音频；上传原件完整保留。视频画面为采样识别。' : props.source.rendition.policy === 'image-pdf/1' ? '当前显示图片转换的 PDF，包含全部 TIFF 页面或 GIF 帧；高亮定位于对应页，上传原件完整保留。' : '当前显示原文件的 PDF 转换版本，高亮定位于该版本。上传的 Office 原件完整保留。'),
      h('button', {type:'button', disabled:busy.value, onClick:download}, busy.value ? '正在下载…' : props.source.rendition.policy === 'office-pdf/1' ? '下载 Office 原件' : '下载原始材料'),
      error.value ? h('p', {role:'alert'}, error.value) : null,
    ]) : null;
  },
});
